import React, { Component } from "react";
import { bool, func, shape, string } from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Col,
} from "reactstrap";
import swal from "sweetalert2";
import { Formik } from "formik";
import recovery from "@/api/recovery";
import LoadingButtonIcon from "@/views/Components/LoadingButtonIcon";
import { handleError } from "@/util/errorHandler";
import styles from "./styles.module.scss";

class PrepareRecoveryModal extends Component {
  state = {
    isSending: false,
    isCodeSended: false,
  };

  sendCode = async () => {
    try {
      this.setState({
        isSending: true,
      });

      await recovery.prepareAccountRecoveryRequest({
        accountId: this.props.userData.accountId,
        name: this.props.userData.fullName,
      });

      this.setState({
        isCodeSended: true,
      });
    } catch (error) {
      handleError(error);
    } finally {
      this.setState({
        isSending: false,
      });
    }
  };

  onClose = async () => {
    if (this.state.code) {
      const isConfirmed = await this.askCloseConfirmation();

      if (!isConfirmed) return;
    }

    this.setState({ isCodeSended: false });
    this.props.onModalClose();
  };

  askCloseConfirmation = async () => {
    const { value } = await swal({
      title: "Are you sure?",
      text: "After closing the window, you will not see the code again.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Close",
      cancelButtonText: "Do not close",
    });

    return value;
  };

  onVerifyRequest = async ({ code }) => {
    try {
      this.setState({ isSending: true });

      const { accountId } = this.props.userData;

      await recovery.verifyAccountRecoveryRequest({
        accountId,
        code,
      });

      this.props.onSuccess();
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isSending: false });
    }
  };

  validateFormik = ({ code }) => {
    const errors = {};

    if (!code) {
      errors.code = "Please enter SMS code";
    }

    return errors;
  };

  render() {
    const { isSending, isCodeSended } = this.state;
    const { isOpen, userData } = this.props;

    const firstStep = (
      <>
        <CardBody>
          <p className={styles.codeDescription}>
            To prepare recovery procedure, please send SMS code to user.
          </p>
        </CardBody>
        <CardFooter>
          <Button loading="isSending" color="primary" onClick={this.sendCode}>
            <span>Send code</span>
            <LoadingButtonIcon loading={isSending} />
          </Button>
        </CardFooter>
      </>
    );

    const secondStep = (
      <Formik
        initialValues={{
          code: "",
        }}
        validate={this.validateFormik}
        onSubmit={this.onVerifyRequest}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          setErrors,
        }) => (
          <Form onSubmit={handleSubmit}>
            <CardBody>
              <FormGroup row>
                <Label sm={3}>Code</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="code"
                    placeholder="Enter code from SMS"
                    invalid={Boolean(errors.code)}
                    value={values.code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.code && (
                    <FormFeedback className="text-alert">
                      {errors.code}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
            </CardBody>
            <CardFooter>
              <div className={styles.footer}>
                <Button color="primary" type="submit">
                  <span>Verify</span>
                  <LoadingButtonIcon loading={isSending} />
                </Button>
                <Button onClick={this.onClose}>Cancel</Button>
              </div>
            </CardFooter>
          </Form>
        )}
      </Formik>
    );

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={this.onClose}
        className={styles.modal}
        overlayClassName={styles.overlay}
        htmlOpenClassName={styles.htmlOpen}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button
              close
              onClick={this.onClose}
              className={styles.closeButton}
            />
            <span>
              Prepare recovery for {userData.fullName || userData.accountId}
            </span>
          </CardHeader>
          {!isCodeSended ? firstStep : secondStep}
        </Card>
      </Modal>
    );
  }
}

PrepareRecoveryModal.propTypes = {
  isOpen: bool.isRequired,
  onModalClose: func.isRequired,
  onSuccess: func.isRequired,
  userData: shape({
    accountId: string,
    fullName: string,
    documentId: string,
  }).isRequired,
};

export default PrepareRecoveryModal;
